import React, { useEffect, useRef, useState } from 'react';
import api from '../lib/api';
import toast from 'react-hot-toast';
import { CalendarClock, Search, Upload, Pencil, X, FileDown } from 'lucide-react';

const formatarData = (iso) => {
  if (!iso) return '—';
  const [ano, mes, dia] = String(iso).slice(0, 10).split('-');
  if (!ano || !mes || !dia) return iso;
  return `${dia}/${mes}/${ano}`;
};

const SITUACAO_LABEL = {
  atrasado: 'Atrasado',
  em_dia: 'Em dia',
  sem_data: 'Sem data',
};

const Preventivas = () => {
  const [itens, setItens] = useState([]);
  const [totais, setTotais] = useState({ total: 0, atrasado: 0, em_dia: 0, sem_data: 0 });
  const [rotas, setRotas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [rota, setRota] = useState('');
  const [situacao, setSituacao] = useState('todos');
  const [semContrato, setSemContrato] = useState(false);
  const [editando, setEditando] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [importando, setImportando] = useState(false);
  const [exportando, setExportando] = useState(false);
  const arquivoRef = useRef(null);

  const carregar = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/preventivas', {
        params: { search, rota, situacao, sem_contrato: semContrato },
      });
      setItens(data.itens || []);
      setTotais(data.totais || { total: 0, atrasado: 0, em_dia: 0, sem_data: 0 });
      setRotas(data.rotas || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao carregar preventivas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(carregar, 250);
    return () => clearTimeout(timer);
  }, [search, rota, situacao, semContrato]);

  const abrirEdicao = (item) => {
    setEditando({
      id: item.id,
      nome: item.nome,
      intervalo_preventiva_dias: item.intervalo_preventiva_dias || 45,
      ultima_preventiva: item.ultima_preventiva || '',
      agenda_preventiva: item.agenda_preventiva || '',
      observacao_preventiva: item.observacao_preventiva || '',
    });
  };

  const salvarEdicao = async (event) => {
    event.preventDefault();
    if (!editando) return;
    setSalvando(true);
    try {
      await api.patch(`/preventivas/${editando.id}`, {
        intervalo_preventiva_dias: Number(editando.intervalo_preventiva_dias),
        ultima_preventiva: editando.ultima_preventiva || null,
        agenda_preventiva: editando.agenda_preventiva,
        observacao_preventiva: editando.observacao_preventiva,
      });
      toast.success('Preventiva atualizada');
      setEditando(null);
      carregar();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSalvando(false);
    }
  };

  const importar = async (event) => {
    const arquivo = event.target.files?.[0];
    event.target.value = '';
    if (!arquivo) return;
    setImportando(true);
    try {
      const form = new FormData();
      form.append('file', arquivo);
      const { data } = await api.post('/preventivas/importar', form);
      const faltando = data.nao_encontrados?.length || 0;
      toast.success(
        `${data.atualizados} cliente(s) atualizado(s) com prazo, observação ou última data` +
          (faltando ? `. ${faltando} linha(s) sem cliente correspondente.` : '.')
      );
      if (faltando) {
        const amostra = data.nao_encontrados
          .slice(0, 5)
          .map((linha) => linha.nome || linha.codigo || linha.serie)
          .filter(Boolean)
          .join(', ');
        if (amostra) toast.error(`Não encontrados: ${amostra}`);
      }
      carregar();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao importar planilha');
    } finally {
      setImportando(false);
    }
  };

  const exportarPdf = async () => {
    setExportando(true);
    try {
      const res = await api.get('/preventivas/pdf', {
        responseType: 'blob',
        params: { search, rota, situacao, sem_contrato: semContrato, t: Date.now() },
        headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'relatorio_preventivas.pdf';
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao exportar PDF');
    } finally {
      setExportando(false);
    }
  };

  const cartoes = [
    { id: 'todos', label: 'Todos', valor: totais.total, classe: 'text-slate-900' },
    { id: 'em_dia', label: 'Em dia', valor: totais.em_dia, classe: 'text-emerald-700' },
    { id: 'atrasado', label: 'Atrasados', valor: totais.atrasado, classe: 'text-red-700' },
    { id: 'sem_data', label: 'Sem data', valor: totais.sem_data, classe: 'text-amber-700' },
  ];

  return (
    <div className="space-y-6 animate-fade-in min-w-0 max-w-full" data-testid="preventivas-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
            <CalendarClock className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Preventivas</h1>
            <p className="text-slate-600">
              A última data vem do relatório. A próxima é essa data mais 45 dias, ou o prazo da exceção.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <input
            ref={arquivoRef}
            type="file"
            accept=".xlsx"
            className="hidden"
            onChange={importar}
          />
          <button
            type="button"
            disabled={exportando}
            onClick={exportarPdf}
            className="bg-emerald-600 text-white px-5 py-3 rounded-xl font-medium hover:bg-emerald-700 transition-all flex items-center gap-2 disabled:opacity-60"
          >
            <FileDown className="w-5 h-5" />
            {exportando ? 'Exportando...' : 'Exportar PDF'}
          </button>
          <button
            type="button"
            disabled={importando}
            onClick={() => arquivoRef.current?.click()}
            title="A planilha atualiza o prazo, a observação e a última data de quem ainda não tem"
            className="bg-white border border-slate-200 text-slate-800 px-5 py-3 rounded-xl font-medium hover:bg-slate-50 transition-all flex items-center gap-2"
          >
            <Upload className="w-5 h-5" />
            {importando ? 'Importando...' : 'Importar exceções'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {cartoes.map((cartao) => (
          <button
            key={cartao.id}
            type="button"
            onClick={() => setSituacao(cartao.id)}
            className={`text-left bg-white rounded-2xl shadow-sm p-4 border ${
              situacao === cartao.id ? 'border-emerald-500' : 'border-transparent'
            }`}
          >
            <p className="text-sm text-slate-500">{cartao.label}</p>
            <p className={`text-2xl font-bold ${cartao.classe}`}>{cartao.valor}</p>
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Buscar laboratório, código, série ou cidade"
            className="w-full pl-9 pr-3 py-3 rounded-xl border border-slate-200 bg-white"
          />
        </div>
        <label className="flex items-center gap-2 px-3 py-3 rounded-xl border border-slate-200 bg-white text-sm text-slate-700 cursor-pointer">
          <input
            type="checkbox"
            checked={semContrato}
            onChange={(event) => setSemContrato(event.target.checked)}
            className="w-4 h-4 text-emerald-600 rounded"
          />
          Sem contrato
        </label>
        <select
          value={rota}
          onChange={(event) => setRota(event.target.value)}
          className="py-3 px-3 rounded-xl border border-slate-200 bg-white min-w-[240px]"
        >
          <option value="">Todas as rotas</option>
          {rotas.map((nome) => (
            <option key={nome} value={nome}>{nome}</option>
          ))}
        </select>
      </div>

      <div className="bg-white rounded-2xl shadow-sm max-w-full overflow-auto max-h-[calc(100dvh-17rem)]">
        <table className="w-full min-w-[1100px]">
          <thead className="sticky top-0 z-10 bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="sticky left-0 z-20 bg-slate-50 text-left py-3 px-3 text-sm font-medium text-slate-600 shadow-[4px_0_8px_-4px_rgba(15,23,42,0.15)]">
                Editar
              </th>
              {['Contrato', 'Código', 'Laboratório', 'Local', 'Cidade', 'Série', 'Última', 'Próxima', 'Intervalo', 'Situação', 'Observação'].map((titulo) => (
                <th key={titulo} className="text-left py-3 px-3 text-sm font-medium text-slate-600">{titulo}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={12} className="py-10 text-center text-slate-500">Carregando...</td>
              </tr>
            )}
            {!loading && itens.length === 0 && (
              <tr>
                <td colSpan={12} className="py-10 text-center text-slate-500">
                  Nenhum cliente neste filtro.
                </td>
              </tr>
            )}
            {!loading && itens.map((item) => (
              <tr
                key={item.id}
                className={`border-b border-slate-100 ${item.situacao === 'atrasado' ? 'bg-red-50' : 'bg-white'}`}
              >
                <td className={`sticky left-0 z-10 py-3 px-3 shadow-[4px_0_8px_-4px_rgba(15,23,42,0.12)] ${item.situacao === 'atrasado' ? 'bg-red-50' : 'bg-white'}`}>
                  <button
                    type="button"
                    onClick={() => abrirEdicao(item)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100"
                  >
                    <Pencil className="w-4 h-4" />
                    Editar
                  </button>
                </td>
                <td className="py-3 px-3 text-sm text-slate-700">{item.m8_contrato_id ?? '—'}</td>
                <td className="py-3 px-3 text-sm text-slate-700">{item.codigo || '—'}</td>
                <td className="py-3 px-3 text-sm font-medium text-slate-900">{item.nome || '—'}</td>
                <td className="py-3 px-3 text-sm text-slate-600 max-w-[220px]">{item.endereco || '—'}</td>
                <td className="py-3 px-3 text-sm text-slate-700">{item.cidade || '—'}</td>
                <td className="py-3 px-3 text-sm text-slate-700">{item.numero_serie || '—'}</td>
                <td className="py-3 px-3 text-sm text-slate-700">{formatarData(item.ultima_preventiva)}</td>
                <td className="py-3 px-3 text-sm text-slate-700">{formatarData(item.proxima_preventiva)}</td>
                <td className="py-3 px-3 text-sm text-slate-700">{item.intervalo_preventiva_dias} dias</td>
                <td className="py-3 px-3 text-sm">
                  <span className={item.situacao === 'atrasado' ? 'font-semibold text-red-700' : 'text-slate-700'}>
                    {SITUACAO_LABEL[item.situacao] || item.situacao}
                    {item.situacao === 'atrasado' ? ` (${item.dias_atraso}d)` : ''}
                  </span>
                </td>
                <td className="py-3 px-3 text-sm text-slate-600 max-w-[220px]">
                  {[item.agenda_preventiva, item.observacao_preventiva].filter(Boolean).join(' · ') || '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editando && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
          <form onSubmit={salvarEdicao} className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="text-lg font-bold text-slate-900">Editar preventiva</h2>
                <p className="text-sm text-slate-500">{editando.nome}</p>
              </div>
              <button type="button" onClick={() => setEditando(null)} className="text-slate-400">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <label className="block text-sm text-slate-600">
                Tempo até a próxima manutenção (dias)
                <input
                  type="number"
                  min="1"
                  max="366"
                  value={editando.intervalo_preventiva_dias}
                  onChange={(event) => setEditando({ ...editando, intervalo_preventiva_dias: event.target.value })}
                  className="mt-1 w-full border border-slate-200 rounded-xl px-3 py-2"
                />
              </label>
              <label className="block text-sm text-slate-600">
                Última preventiva
                <input
                  type="date"
                  value={editando.ultima_preventiva || ''}
                  onChange={(event) => setEditando({ ...editando, ultima_preventiva: event.target.value })}
                  className="mt-1 w-full border border-slate-200 rounded-xl px-3 py-2"
                />
              </label>
            </div>
            <label className="block text-sm text-slate-600">
              Agenda
              <input
                value={editando.agenda_preventiva}
                onChange={(event) => setEditando({ ...editando, agenda_preventiva: event.target.value })}
                className="mt-1 w-full border border-slate-200 rounded-xl px-3 py-2"
              />
            </label>
            <label className="block text-sm text-slate-600">
              Observação
              <textarea
                placeholder="Ex.: avisar antes, acesso pelo portão lateral"
                value={editando.observacao_preventiva}
                onChange={(event) => setEditando({ ...editando, observacao_preventiva: event.target.value })}
                className="mt-1 w-full border border-slate-200 rounded-xl px-3 py-2"
                rows={3}
              />
            </label>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setEditando(null)} className="px-4 py-2 rounded-xl text-slate-600">
                Cancelar
              </button>
              <button
                type="submit"
                disabled={salvando}
                className="px-5 py-2 rounded-xl bg-emerald-600 text-white font-medium disabled:opacity-60"
              >
                {salvando ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default Preventivas;
